import type { QuoteAccessoryParameters } from '../powershadesApiTypes';
import type {
	AwningConfigurationRequest,
	AwningConfigurationResponse,
	AwningFabric,
	AwningPriceBreakdown,
	AwningPricingGroup,
	AwningProperty,
} from './awningTypes';

const WALL_PITCHES = ["5-40 DEGREES WALL", "40-65 DEGREES WALL"];
const SOFFIT_PITCH = "5-85 DEGREES SOFFIT";

export const MOUNTING_OPTION_PROPERTY_NUMBER = 6;
export const ROOF_MOUNT_BRACKETS_PROPERTY_NUMBER = 7;
export const MOUNTING_BRACKETS_PROPERTY_NUMBER = 8;
export const REMOTE_PROPERTY_NUMBER = 12;
export const SENSOR_PROPERTY_NUMBER = 13;
const FABRIC_BRAND_PROPERTY_NUMBER = 15;
const FABRIC_NUMBER_PROPERTY_NUMBER = 16;

/**
 * Properties whose configuration value is a list rather than a single string. These are stored on
 * the quote as a JSON encoded array and rendered with the checkbox multi-select instead of a dropdown.
 */
const MULTI_SELECT_PROPERTY_NUMBERS = [REMOTE_PROPERTY_NUMBER, SENSOR_PROPERTY_NUMBER];

export const isMultiSelectPropertyNumber = (propertyNumber: number): boolean => (
	MULTI_SELECT_PROPERTY_NUMBERS.includes(propertyNumber)
);

const asRecord = (value: unknown): Record<string, unknown> | null => (
	typeof value === 'object' && value !== null && !Array.isArray(value)
		? value as Record<string, unknown>
		: null
);

export const normalizeAwningList = <T>(data: unknown, key?: string): T[] => {
	if (Array.isArray(data)) {
		return data as T[];
	}

	const record = asRecord(data);
	if (!record) {
		return [];
	}

	if (key && Array.isArray(record[key])) {
		return record[key] as T[];
	}

	if (Array.isArray(record.data)) {
		return record.data as T[];
	}

	return [];
};

export const normalizeAwningProperty = (property: Record<string, unknown>): AwningProperty => ({
	property_number: Number(property.property_number ?? property.propertyNumber ?? 0),
	property_name: String(property.property_name ?? property.propertyName ?? ''),
	is_multi_select: Boolean(property.is_multi_select ?? property.isMultiSelect ?? false),
	is_required: Boolean(property.is_required ?? property.isRequired ?? false),
	options: (Array.isArray(property.options) ? property.options : []).map((option) => {
		const rawOption = option as Record<string, unknown>;
		return {
			index: Number(rawOption.index ?? 0),
			value: String(rawOption.value ?? ''),
			is_default: Boolean(rawOption.is_default ?? rawOption.isDefault ?? false),
			is_required: Boolean(rawOption.is_required ?? rawOption.isRequired ?? false),
		};
	}),
});

export const normalizeAwningFabric = (fabric: Record<string, unknown>): AwningFabric => ({
	color_code: String(fabric.color_code ?? fabric.colorCode ?? ''),
	color_name: String(fabric.color_name ?? fabric.colorName ?? ''),
	color_group: String(fabric.color_group ?? fabric.colorGroup ?? ''),
	fabric_brand: String(fabric.fabric_brand ?? fabric.fabricBrand ?? ''),
	ke_fabric_code: String(fabric.ke_fabric_code ?? fabric.keFabricCode ?? ''),
	is_solid: Boolean(fabric.is_solid ?? fabric.isSolid ?? false),
	is_stripe: Boolean(fabric.is_stripe ?? fabric.isStripe ?? false),
});

export const normalizeAwningProperties = (data: unknown): AwningProperty[] => (
	normalizeAwningList<Record<string, unknown>>(data, 'options').map(normalizeAwningProperty)
);

export const normalizeAwningFabrics = (data: unknown): AwningFabric[] => (
	normalizeAwningList<Record<string, unknown>>(data, 'fabrics').map(normalizeAwningFabric)
);

const normalizeAwningPriceBreakdown = (value: unknown): AwningPriceBreakdown => {
	const breakdown = asRecord(value) ?? {};

	const rawSensorAdders = breakdown.sensor_adders ?? breakdown.sensorAdders;
	const rawRemoteAdders = breakdown.remote_adders ?? breakdown.remoteAdders;

	return {
		base_price: Number(breakdown.base_price ?? breakdown.basePrice ?? 0),
		bracket_adder: Number(breakdown.bracket_adder ?? breakdown.bracketAdder ?? 0),
		freight_adder: Number(breakdown.freight_adder ?? breakdown.freightAdder ?? 0),
		arms_with_led_adder: Number(breakdown.arms_with_led_adder ?? breakdown.armsWithLedAdder ?? 0),
		total_sensor_adder: Number(breakdown.total_sensor_adder ?? breakdown.totalSensorAdder ?? 0),
		sensor_adders: (Array.isArray(rawSensorAdders) ? rawSensorAdders : []).map((sensor) => {
			const rawSensor = sensor as Record<string, unknown>;
			return {
				sensor_option: String(rawSensor.sensor_option ?? rawSensor.sensorOption ?? ''),
				adder: Number(rawSensor.adder ?? 0),
			};
		}),
		total_remote_adder: Number(breakdown.total_remote_adder ?? breakdown.totalRemoteAdder ?? 0),
		remote_adders: (Array.isArray(rawRemoteAdders) ? rawRemoteAdders : []).map((remote) => {
			const rawRemote = remote as Record<string, unknown>;
			return {
				remote_option: String(rawRemote.remote_option ?? rawRemote.remoteOption ?? ''),
				adder: Number(rawRemote.adder ?? 0),
			};
		}),
	};
};

const normalizeAwningPricingGroup = (value: unknown): AwningPricingGroup | undefined => {
	const group = asRecord(value);
	if (!group) return undefined;

	return {
		breakdown: normalizeAwningPriceBreakdown(group.breakdown),
		total_price: Number(group.total_price ?? group.totalPrice ?? 0),
	};
};

export const normalizeAwningConfigureResponse = (data: unknown): AwningConfigurationResponse => {
	const record = asRecord(data) ?? {};

	const msrpPricing = normalizeAwningPricingGroup(record.msrp_pricing ?? record.msrpPricing);
	const costPricing = normalizeAwningPricingGroup(
		record.cost_pricing ?? record.costPricing ?? record.customer_pricing ?? record.customerPricing,
	);
	const dealerPricing = normalizeAwningPricingGroup(record.dealer_pricing ?? record.dealerPricing);
	const distributorPricing = normalizeAwningPricingGroup(record.distributor_pricing ?? record.distributorPricing);

	return {
		is_valid: Boolean(record.is_valid ?? record.isValid ?? false),
		errors: Array.isArray(record.errors) ? record.errors.map((error) => String(error)) : [],
		...(msrpPricing ? { msrp_pricing: msrpPricing } : {}),
		...(costPricing ? { cost_pricing: costPricing } : {}),
		...(dealerPricing ? { dealer_pricing: dealerPricing } : {}),
		...(distributorPricing ? { distributor_pricing: distributorPricing } : {}),
	};
};

export const buildInitialConfiguration = (
	properties: AwningProperty[],
	fabric?: AwningFabric | null,
): AwningConfigurationRequest => {
	const config: AwningConfigurationRequest = {
		select_unit_type: '',
		width: '',
		projection: '',
		arms_with_led: '',
		frame_color: '',
		mounting_option: '',
		roof_mount_brackets: 'NONE',
		mounting_brackets: 'NONE',
		drive_side: '',
		operation: '',
		motor_cable: '',
		remote: [],
		sensors: [],
		crank: '',
		fabric_brand: fabric?.fabric_brand ?? '',
		fabric_number: fabric?.ke_fabric_code ?? '',
		desired_pitch: '',
	};

	properties.forEach((property) => {
		if (
			property.property_number === FABRIC_BRAND_PROPERTY_NUMBER
			|| property.property_number === FABRIC_NUMBER_PROPERTY_NUMBER
		) {
			return;
		}

		// Sensors are optional and start empty; the remote starts with its default/required options
		// (every awning ships with one BOND MATE PRO remote that cannot be removed).
		if (property.property_number === REMOTE_PROPERTY_NUMBER) {
			config.remote = getDefaultMultiSelectValues(property);
			return;
		}
		if (property.property_number === SENSOR_PROPERTY_NUMBER) {
			return;
		}

		const field = getConfigFieldForProperty(property.property_number);
		if (!field) return;

		const defaultOption = property.options.find((o) => o.is_default) ?? property.options[0];
		if (defaultOption) {
			config[field] = defaultOption.value as never;
		}
	});

	if (fabric) {
		config.fabric_brand = fabric.fabric_brand;
		config.fabric_number = fabric.ke_fabric_code;
	}

	applyMountingDefaults(config);
	return config;
};

export const getConfigFieldForProperty = (propertyNumber: number): keyof AwningConfigurationRequest | null => {
	const map: Record<number, keyof AwningConfigurationRequest> = {
		1: 'select_unit_type',
		2: 'width',
		3: 'projection',
		4: 'arms_with_led',
		5: 'frame_color',
		6: 'mounting_option',
		7: 'roof_mount_brackets',
		8: 'mounting_brackets',
		9: 'drive_side',
		10: 'operation',
		11: 'motor_cable',
		12: 'remote',
		14: 'crank',
		15: 'fabric_brand',
		16: 'fabric_number',
		17: 'desired_pitch',
	};
	return map[propertyNumber] ?? null;
};

const normalizeOptionValue = (value: string): string => value.trim().toUpperCase();

const isSameOptionValue = (a: string, b: string): boolean => (
	normalizeOptionValue(a) === normalizeOptionValue(b)
);

/**
 * Option values that a multi-select property must always include. The API rejects any selection
 * that drops one of these, so the form re-adds them whenever the user tries to remove them.
 */
export const getRequiredOptionValues = (property: AwningProperty | undefined): string[] => (
	property?.options.filter((o) => o.is_required).map((o) => o.value) ?? []
);

/**
 * Initial selection for a multi-select property: every default option plus every required option,
 * in the property's option order.
 */
export const getDefaultMultiSelectValues = (property: AwningProperty | undefined): string[] => (
	property?.options.filter((o) => o.is_default || o.is_required).map((o) => o.value) ?? []
);

/**
 * Cleans up a multi-select selection so the API will accept it:
 * - required options are always present (re-added in option order if the user removed them),
 * - duplicates are collapsed,
 * - when the property's options are known, values that no longer exist are dropped (an option may
 *   have been renamed in the admin UI since the awning was quoted).
 */
export const ensureRequiredOptionValues = (
	property: AwningProperty | undefined,
	selected: string[],
): string[] => {
	const cleaned = (selected ?? [])
		.map((value) => (value ?? '').trim())
		.filter((value) => value && normalizeOptionValue(value) !== 'NONE');

	const known = property && property.options.length > 0
		? cleaned.filter((value) => property.options.some((o) => isSameOptionValue(o.value, value)))
		: cleaned;

	const result: string[] = [];
	getRequiredOptionValues(property).forEach((required) => {
		if (!result.some((value) => isSameOptionValue(value, required))) {
			result.push(required);
		}
	});
	known.forEach((value) => {
		if (!result.some((existing) => isSameOptionValue(existing, value))) {
			result.push(value);
		}
	});

	return result;
};

/**
 * Reads a multi-select awning parameter. New awnings store these as a JSON encoded array; awnings
 * quoted before a property became multi-select hold a plain string, which is treated as a single
 * selection.
 */
export const parseAwningMultiSelectValue = (raw: string | undefined | null): string[] => {
	const value = (raw ?? '').trim();
	if (!value) return [];

	if (value.startsWith('[')) {
		try {
			const parsed = JSON.parse(value);
			if (Array.isArray(parsed)) {
				return parsed.map((item) => `${item}`.trim()).filter((item) => item);
			}
		} catch {
			// Fall through and treat the raw value as a single selection.
		}
	}

	return [value];
};

export const getAwningMultiSelectParamValues = (
	parameters: QuoteAccessoryParameters[] | undefined,
	name: string,
): string[] => parseAwningMultiSelectValue(getAwningParamValue(parameters, name));

export const shouldShowProperty = (property: AwningProperty): boolean => {
	if (isMultiSelectPropertyNumber(property.property_number)) return true;
	if (
		property.property_number === FABRIC_BRAND_PROPERTY_NUMBER
		|| property.property_number === FABRIC_NUMBER_PROPERTY_NUMBER
	) {
		return false;
	}
	return property.options.length > 1;
};

export const getMountingOptionIndex = (
	mountingProperty: AwningProperty | undefined,
	mountingOptionValue: string,
): number | null => {
	if (!mountingOptionValue || !mountingProperty) return null;

	const option = mountingProperty.options.find((o) => o.value === mountingOptionValue);
	return option?.index ?? null;
};

/**
 * Standard mounting brackets are always required, whatever the mounting option. A ROOF mount
 * additionally needs roof mount brackets, which sit under the standard brackets rather than
 * replacing them, so both bracket pickers are shown for ROOF.
 */
export const shouldShowBracketProperty = (
	propertyNumber: number,
	mountingProperty: AwningProperty | undefined,
	mountingOptionValue: string,
): boolean => {
	if (propertyNumber === MOUNTING_BRACKETS_PROPERTY_NUMBER) return true;

	if (propertyNumber === ROOF_MOUNT_BRACKETS_PROPERTY_NUMBER) {
		return isRoofMounting(mountingProperty, mountingOptionValue);
	}

	return true;
};

export const shouldShowAwningFormProperty = (
	property: AwningProperty,
	mountingProperty: AwningProperty | undefined,
	mountingOptionValue: string,
): boolean => {
	if (!shouldShowProperty(property)) return false;

	if (
		property.property_number === ROOF_MOUNT_BRACKETS_PROPERTY_NUMBER
		|| property.property_number === MOUNTING_BRACKETS_PROPERTY_NUMBER
	) {
		return shouldShowBracketProperty(property.property_number, mountingProperty, mountingOptionValue);
	}

	return true;
};

export const getPitchOptions = (mountingOption: string, property: AwningProperty): AwningProperty['options'] => {
	const mounting = mountingOption.toUpperCase();
	if (mounting === 'SOFFIT') {
		return property.options.filter((o) => o.value.toUpperCase().includes('SOFFIT'));
	}
	return property.options.filter((o) => !o.value.toUpperCase().includes('SOFFIT'));
};

type ApplyMountingDefaultsOptions = {
	autoFillBrackets?: boolean;
};

/**
 * ROOF is the only mounting option that uses roof mount brackets. It is identified by value when
 * possible and falls back to the option index (WALL 0, SOFFIT 1, ROOF 2+) so a renamed option still
 * behaves sensibly.
 */
export const isRoofMounting = (
	mountingProperty: AwningProperty | undefined,
	mountingOptionValue: string,
): boolean => {
	if (!mountingOptionValue) return false;
	if (mountingOptionValue.trim().toUpperCase() === 'ROOF') return true;

	const index = getMountingOptionIndex(mountingProperty, mountingOptionValue);
	return index !== null && index >= 2;
};

/**
 * Standard mounting brackets apply to every mounting option, so they survive a mounting change.
 * Roof mount brackets only apply to ROOF and are cleared when leaving it.
 */
export const clearBracketSelectionsOnMountingChange = (
	config: AwningConfigurationRequest,
	mountingProperty: AwningProperty | undefined,
	previousMountingOption: string,
): void => {
	const wasRoof = isRoofMounting(mountingProperty, previousMountingOption);
	const isRoof = isRoofMounting(mountingProperty, config.mounting_option);

	if (wasRoof && !isRoof && config.roof_mount_brackets && config.roof_mount_brackets !== 'NONE') {
		config.roof_mount_brackets = '';
	}
};

export const applyMountingDefaults = (
	config: AwningConfigurationRequest,
	options: ApplyMountingDefaultsOptions = {},
): void => {
	const autoFillBrackets = options.autoFillBrackets ?? true;
	const mounting = (config.mounting_option ?? '').toUpperCase();

	if (!mounting) {
		config.roof_mount_brackets = autoFillBrackets ? 'NONE' : '';
		config.mounting_brackets = autoFillBrackets ? 'NONE' : '';
		return;
	}

	// Standard mounting brackets are required for every mounting option.
	if (autoFillBrackets && (!config.mounting_brackets || config.mounting_brackets === 'NONE')) {
		config.mounting_brackets = 'TWO 8"';
	}

	if (mounting === 'ROOF') {
		// Roof mount brackets are used together with the standard mounting brackets.
		if (autoFillBrackets && (!config.roof_mount_brackets || config.roof_mount_brackets === 'NONE')) {
			config.roof_mount_brackets = '12"';
		}
		if (!config.desired_pitch || config.desired_pitch.toUpperCase().includes('SOFFIT')) {
			config.desired_pitch = WALL_PITCHES[0] ?? '';
		}
	} else if (mounting === 'WALL' || mounting === 'SOFFIT') {
		config.roof_mount_brackets = 'NONE';
		if (mounting === 'SOFFIT') {
			config.desired_pitch = SOFFIT_PITCH;
		} else if (config.desired_pitch.toUpperCase().includes('SOFFIT')) {
			config.desired_pitch = WALL_PITCHES[0] ?? '';
		}
	}
};

export const applyMountingDefaultsToParameters = (
	parameters: QuoteAccessoryParameters[],
): QuoteAccessoryParameters[] => {
	const config = parametersToConfiguration(parameters);
	applyMountingDefaults(config);
	const colorName = getAwningParamValue(parameters, 'colorName');
	return configurationToQuoteParameters(config, colorName || undefined);
};

export const configurationToApiPayload = (config: AwningConfigurationRequest) => ({
	selectUnitType: config.select_unit_type,
	width: config.width,
	projection: config.projection,
	armsWithLed: config.arms_with_led,
	frameColor: config.frame_color,
	mountingOption: config.mounting_option,
	roofMountBrackets: config.roof_mount_brackets,
	mountingBrackets: config.mounting_brackets,
	driveSide: config.drive_side,
	operation: config.operation,
	motorCable: config.motor_cable,
	remote: config.remote,
	sensors: config.sensors,
	crank: config.crank,
	fabricBrand: config.fabric_brand,
	fabricNumber: config.fabric_number,
	desiredPitch: config.desired_pitch,
});

export const configurationToQuoteParameters = (
	config: AwningConfigurationRequest,
	colorName?: string,
): QuoteAccessoryParameters[] => {
	const awningParm = (
		name: string,
		value: string,
		description: string,
	): QuoteAccessoryParameters => ({
		name,
		value,
		description,
		unit_of_measurement: 'Each',
	});

	const parms: QuoteAccessoryParameters[] = [
		awningParm('selectUnitType', config.select_unit_type, 'SELECT UNIT TYPE'),
		awningParm('width', config.width, 'UNIT WIDTH'),
		awningParm('projection', config.projection, 'PROJECTION'),
		awningParm('armsWithLed', config.arms_with_led, 'ARMS WITH LED'),
		awningParm('frameColor', config.frame_color, 'FRAME COLOR'),
		awningParm('mountingOption', config.mounting_option, 'MOUNTING OPTION'),
		awningParm('roofMountBrackets', config.roof_mount_brackets, 'ROOF MOUNT BRACKETS'),
		awningParm('mountingBrackets', config.mounting_brackets, 'MOUNTING BRACKETS'),
		awningParm('driveSide', config.drive_side, 'DRIVE SIDE'),
		awningParm('operation', config.operation, 'OPERATION'),
		awningParm('motorCable', config.motor_cable, 'MOTOR CABLE'),
		awningParm('remote', JSON.stringify(config.remote ?? []), 'REMOTE'),
		awningParm('sensors', JSON.stringify(config.sensors ?? []), 'SENSOR'),
		awningParm('crank', config.crank, 'CRANK'),
		awningParm('fabricBrand', config.fabric_brand, 'FABRIC BRAND'),
		awningParm('fabricNumber', config.fabric_number, 'FABRIC NUMBER'),
		awningParm('desiredPitch', config.desired_pitch, 'DESIRED PITCH'),
	];

	if (colorName) {
		parms.push(awningParm('colorName', colorName, 'Fabric Color'));
	}

	return parms;
};

export const isConfigurationComplete = (config: AwningConfigurationRequest): boolean => {
	return Boolean(
		config.select_unit_type
		&& config.width
		&& config.projection
		&& config.arms_with_led
		&& config.frame_color
		&& config.mounting_option
		&& config.drive_side
		&& config.operation
		&& config.motor_cable
		&& (config.remote ?? []).length > 0
		&& config.crank
		&& config.fabric_brand
		&& config.fabric_number
		&& config.desired_pitch
	);
};

export const getAwningParamValue = (
	parameters: QuoteAccessoryParameters[] | undefined,
	name: string,
): string => parameters?.find((p) => p.name === name)?.value ?? '';

export const replaceAwningParamValues = (
	parameters: QuoteAccessoryParameters[],
	updates: Record<string, { value: string; description?: string }>,
): QuoteAccessoryParameters[] => {
	const next: QuoteAccessoryParameters[] = parameters.map((parm) => ({ ...parm }));

	Object.entries(updates).forEach(([name, update]) => {
		const existingIndex = next.findIndex((p) => p.name === name);
		if (existingIndex >= 0) {
			const existing = next.at(existingIndex);
			if (existing) {
				next[existingIndex] = {
					name: existing.name,
					value: update.value,
					description: update.description ?? existing.description,
					unit_of_measurement: existing.unit_of_measurement || 'Each',
				};
			}
		} else {
			next.push({
				name,
				value: update.value,
				description: update.description ?? name,
				unit_of_measurement: 'Each',
			});
		}
	});

	return next;
};

export const parametersToConfiguration = (
	parameters: QuoteAccessoryParameters[],
): AwningConfigurationRequest => {
	const sensors = getAwningMultiSelectParamValues(parameters, 'sensors');
	const remote = getAwningMultiSelectParamValues(parameters, 'remote');

	return {
		select_unit_type: getAwningParamValue(parameters, 'selectUnitType'),
		width: getAwningParamValue(parameters, 'width'),
		projection: getAwningParamValue(parameters, 'projection'),
		arms_with_led: getAwningParamValue(parameters, 'armsWithLed'),
		frame_color: getAwningParamValue(parameters, 'frameColor'),
		mounting_option: getAwningParamValue(parameters, 'mountingOption'),
		roof_mount_brackets: getAwningParamValue(parameters, 'roofMountBrackets') || 'NONE',
		mounting_brackets: getAwningParamValue(parameters, 'mountingBrackets') || 'NONE',
		drive_side: getAwningParamValue(parameters, 'driveSide'),
		operation: getAwningParamValue(parameters, 'operation'),
		motor_cable: getAwningParamValue(parameters, 'motorCable'),
		remote,
		sensors,
		crank: getAwningParamValue(parameters, 'crank'),
		fabric_brand: getAwningParamValue(parameters, 'fabricBrand'),
		fabric_number: getAwningParamValue(parameters, 'fabricNumber'),
		desired_pitch: getAwningParamValue(parameters, 'desiredPitch'),
	};
};

export const generateDuplicateShadeName = (baseName: string, existingNames: string[]): string => {
	const trimmed = (baseName ?? '').trim() || 'Awning';
	const nameParts = trimmed.split(/\s+/);
	const lastPart = nameParts[nameParts.length - 1] ?? '';
	let newName = trimmed;

	if (!Number.isNaN(Number(lastPart)) && lastPart !== '') {
		let nextNumber = parseInt(lastPart, 10) + 1;
		do {
			nameParts[nameParts.length - 1] = String(nextNumber);
			newName = nameParts.join(' ');
			nextNumber += 1;
		} while (existingNames.includes(newName.trim()));
	} else {
		let nextNumber = 1;
		newName = `${trimmed} ${nextNumber}`;
		while (existingNames.includes(newName.trim())) {
			nextNumber += 1;
			newName = `${trimmed} ${nextNumber}`;
		}
	}

	return newName;
};

export type AwningPriceTier = 'msrp' | 'cost' | 'dealer' | 'distributor';

export type AwningQuotedPriceFields = {
	msrp?: number;
	cost?: number;
	dealer_sale_price?: number;
	base_sale_price?: number;
};

export const getAwningPriceTierFromMultiplierName = (name: string | undefined): AwningPriceTier => {
	const normalized = (name ?? '').toLowerCase();
	if (normalized.includes('cost')) return 'cost';
	if (normalized.includes('dealer')) return 'dealer';
	if (normalized.includes('distributor') || normalized === 'dist') return 'distributor';
	return 'msrp';
};

export const getAwningTierPrice = (
	awning: AwningQuotedPriceFields,
	tier: AwningPriceTier,
): number => {
	switch (tier) {
		case 'cost':
			return awning.cost ?? 0;
		case 'dealer':
			return awning.dealer_sale_price ?? 0;
		case 'distributor':
			return awning.base_sale_price ?? 0;
		default:
			return awning.msrp ?? 0;
	}
};

export const getAwningPriceForMultiplier = (
	awning: AwningQuotedPriceFields,
	multiplierValue: number,
	dealerMultiplier: number,
	distributorMultiplier: number,
): number => {
	if (Math.abs(multiplierValue - 1) < 0.0001) {
		return getAwningTierPrice(awning, 'msrp');
	}
	if (Math.abs(multiplierValue - dealerMultiplier) < 0.0001) {
		return getAwningTierPrice(awning, 'dealer');
	}
	if (Math.abs(multiplierValue - distributorMultiplier) < 0.0001) {
		return getAwningTierPrice(awning, 'distributor');
	}
	return getAwningTierPrice(awning, 'msrp');
};

export const getAwningConfigureTierPrice = (
	configure: AwningConfigurationResponse | null | undefined,
	tier: AwningPriceTier,
): number => {
	const group = tier === 'cost'
		? configure?.cost_pricing
		: tier === 'dealer'
			? configure?.dealer_pricing
			: tier === 'distributor'
				? configure?.distributor_pricing
				: configure?.msrp_pricing;
	return group?.total_price ?? 0;
};

